import { inject } from "mobx-react";
import { useCallback, useState } from "react";
import { SubmitHandler, useFormContext } from "react-hook-form";
import type { Store } from "@inferno/renderer-shared-core";

import { isPWSSessionBackend } from "@inferno/renderer-shared-core";
import { Button, ButtonKind } from "../../../../../core/ui";
import { FORM_ERRORS, SimpleInput } from "../../forms";
import { useCoastAlerts } from "../common/coast-alert/CoastAlerts.hook";
import { CoastAlertsDisplay } from "../common/coast-alert/CoastAlertsDisplay.components";
import { UsersButton } from "../common/UsersButton.components";

interface LoginFormInputs {
  password: string;
  email: string;
}

interface CoastLoginFormProps {
  forgotPassword: () => void;
  passwordHint: () => void;
  store?: Store;
}

export const CoastLoginForm = inject("store")(({ store, forgotPassword, passwordHint }: CoastLoginFormProps) => {
  const { errors, register, handleSubmit } = useFormContext<LoginFormInputs>();

  const { alerts, setErrorAlert, clearAllAlerts } = useCoastAlerts();
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const session = store?.session?.currentSession;

  if (!isPWSSessionBackend(session)) {
    return null;
  }

  const onSubmit: SubmitHandler<LoginFormInputs> = useCallback(
    async ({ email, password }, e) => {
      e?.preventDefault();
      clearAllAlerts();

      setIsLoggingIn(true);

      const error = await session.doLogin(email, password);

      if (error) {
        setErrorAlert(error);
        setIsLoggingIn(false);
      } else {
        session.hideUsersPopup();
      }
    },
    [clearAllAlerts, session, setErrorAlert],
  );

  return (
    <section className="component-forgot-password">
      <CoastAlertsDisplay alerts={alerts} />

      <form onSubmit={handleSubmit(onSubmit)}>
        <SimpleInput
          placeholder="Enter your email"
          rules={{ required: FORM_ERRORS.required }}
          error={errors.email}
          register={register}
          label="Email"
          name="email"
          type="email"
        />

        <SimpleInput
          placeholder="Enter your password"
          rules={{ required: FORM_ERRORS.required }}
          error={errors.password}
          register={register}
          label={
            <span>
              Password &nbsp;
              <Button
                kind={ButtonKind.CUSTOMIZED}
                className="coast-button-text coast-button-inline"
                click={passwordHint}
              >
                Get Hint
              </Button>
            </span>
          }
          name="password"
          type="password"
        />

        <UsersButton
          disabled={!!errors.email || !!errors.password || isLoggingIn}
          isProcessing={isLoggingIn}
          className="button-login"
          type="submit"
          label="Login"
        >
          Log In
        </UsersButton>
      </form>

      <Button
        label="Forgot Password?"
        click={forgotPassword}
        kind={ButtonKind.CUSTOMIZED}
        className="coast-button-text"
      >
        Forgot Password?
      </Button>
    </section>
  );
});
